"use client"

import { COMPANY } from "@/lib/constants"
import { motion } from "framer-motion"
import { ArrowRight, Calendar, Check, CheckCircle, Clock, MapPin, Package, Phone, Shield, Sparkles, Truck, Users } from "lucide-react"
interface ProcessStep {
  iconName: string
  title: string
  description: string
}
const processSteps:ProcessStep[] = [
  { iconName: "Phone", title: "Get Quote", description: "Contact us for a free consultation" },
  { iconName: "Calendar", title: "Schedule", description: "Choose a convenient date" },
  { iconName: "Truck", title: "Packing", description: "Professional packing service" },
  { iconName: "MapPin", title: "Transport", description: "Safe and secure transport" },
  { iconName: "Check", title: "Delivery", description: "Careful unpacking and setup" },
]
export function ServicesProcessSection() {
  // Structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to Move with Bigwin Relocation",
    "description": "Our proven 5-step process ensures your relocation is handled with precision, care, and complete transparency from start to finish",
    "totalTime": "PT48H",
    "estimatedCost": {
      "@type": "MonetaryAmount",
      "currency": "INR",
      "value": "10000"
    },
    "step": processSteps.map((step, index) => ({
      "@type": "HowToStep",
      "position": index + 1,
      "name": step.title,
      "text": step.description,
      "url": `${COMPANY.website}/services#step-${index + 1}`
    }))
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <section
        className="py-20 sm:py-32 bg-gradient-to-br from-slate-50 via-white to-blue-50/30 relative overflow-hidden"
        aria-labelledby="process-heading"
      >
        {/* Background Elements */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-[#FF7A00] rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-[#003DA5] rounded-full blur-3xl"></div>
        </div>

        {/* Floating Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            className="absolute top-20 left-10 w-4 h-4 bg-[#FF7A00]/20 rounded-full"
            animate={{ y: [0, -20, 0], opacity: [0.3, 0.8, 0.3] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute top-40 right-20 w-6 h-6 bg-[#003DA5]/20 rounded-full"
            animate={{ y: [0, 20, 0], opacity: [0.2, 0.6, 0.2] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          />
          <motion.div
            className="absolute bottom-32 left-1/3 w-3 h-3 bg-[#FF7A00]/30 rounded-full"
            animate={{ y: [0, -15, 0], opacity: [0.4, 0.9, 0.4] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 2 }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            className="text-center mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <motion.div
              className="inline-flex items-center gap-2 bg-gradient-to-r from-[#FF7A00]/10 to-[#003DA5]/10 backdrop-blur-sm px-4 py-2 rounded-full border border-[#FF7A00]/20 text-sm font-medium text-[#FF7A00] mb-6"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Sparkles className="w-4 h-4" />
              Simple & Streamlined Process
            </motion.div>

            <h2
              id="process-heading"
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight"
            >
              How We Make Your{" "}
              <span className="bg-gradient-to-r from-[#FF7A00] to-[#003DA5] bg-clip-text text-transparent">
                Move Seamless
              </span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Our proven 5-step process ensures your relocation is handled with precision,
              care, and complete transparency from start to finish
            </p>
          </motion.div>

          {/* Process Steps */}
          <div className="relative">
            {/* Desktop Connection Line */}
            <div className="hidden lg:block absolute top-20 left-0 right-0 h-1 bg-gradient-to-r from-[#FF7A00]/20 via-[#003DA5]/40 to-[#FF7A00]/20 rounded-full -z-10">
              <motion.div
                className="h-full bg-gradient-to-r from-[#FF7A00] to-[#003DA5] rounded-full origin-left"
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 2, delay: 0.5, ease: "easeInOut" }}
              />
            </div>

            <motion.div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-6"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              {processSteps.map((step, index) => {
                // Map icon names to actual icon components
                const iconMap: { [key: string]: any } = {
                  'Phone': Phone,
                  'Calendar': Calendar,
                  'Truck': Truck,
                  'MapPin': MapPin,
                  'Check': Check,
                  'Package': Package,
                  'Shield': Shield,
                  'Users': Users,
                  'Clock': Clock
                }
                const StepIcon = iconMap[step.iconName] || Check
                const isEven = index % 2 === 0

                return (
                  <motion.div
                    key={index}
                    className="relative group"
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.15 }}
                    whileHover={{ y: -8 }}
                  >
                    {/* Step Number */}
                    <motion.div
                      className="absolute -top-4 -right-2 w-8 h-8 bg-gradient-to-br from-[#FF7A00] to-orange-600 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-lg z-10"
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: index * 0.15 + 0.3 }}
                    >
                      {index + 1}
                    </motion.div>

                    {/* Card */}
                    <article
                      className={`relative p-8 bg-white/90 backdrop-blur-sm rounded-2xl border-2 border-white/50 shadow-xl hover:shadow-2xl transition-all duration-500 h-full group-hover:border-[#003DA5]/30 ${isEven ? 'lg:mt-0' : 'lg:mt-12'
                        }`}
                      id={`step-${index + 1}`}
                      itemScope
                      itemType="https://schema.org/HowToStep"
                    >

                      {/* Gradient Glow Effect */}
                      <div className="absolute inset-0 bg-gradient-to-br from-[#003DA5]/5 to-[#FF7A00]/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                      {/* Icon Container */}
                      <motion.div
                        className="relative mb-6"
                        whileHover={{ rotate: 10, scale: 1.1 }}
                        transition={{ duration: 0.3 }}
                      >
                        <div className="w-16 h-16 bg-gradient-to-br from-[#003DA5] to-blue-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300 relative overflow-hidden">
                          <StepIcon size={32} className="text-white relative z-10" />

                          {/* Icon Background Animation */}
                          <motion.div
                            className="absolute inset-0 bg-gradient-to-br from-[#FF7A00]/20 to-transparent"
                            initial={{ scale: 0, opacity: 0 }}
                            whileHover={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.3 }}
                          />
                        </div>

                        {/* Floating Ring */}
                        <motion.div
                          className="absolute inset-0 border-2 border-[#FF7A00]/30 rounded-2xl"
                          animate={{ rotate: 360 }}
                          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                        />
                      </motion.div>

                      {/* Content */}
                      <div className="relative z-10 space-y-4">
                        <h3
                          className="text-lg font-bold text-foreground group-hover:text-[#003DA5] transition-colors duration-300"
                          itemProp="name"
                        >
                          {step.title}
                        </h3>
                        <p
                          className="text-sm text-muted-foreground leading-relaxed"
                          itemProp="text"
                        >
                          {step.description}
                        </p>

                        {/* Progress Indicator */}
                        <div className="flex items-center gap-2 pt-2">
                          <div className="flex-1 h-1 bg-slate-200 rounded-full overflow-hidden">
                            <motion.div
                              className="h-full bg-gradient-to-r from-[#003DA5] to-[#FF7A00]"
                              initial={{ width: 0 }}
                              whileInView={{ width: "100%" }}
                              viewport={{ once: true }}
                              transition={{ duration: 1, delay: index * 0.2 + 0.5 }}
                            />
                          </div>
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        </div>
                      </div>

                      {/* Arrow for Desktop */}
                      {index < processSteps.length - 1 && (
                        <motion.div
                          className="hidden lg:block absolute -right-4 top-1/2 -translate-y-1/2 z-20"
                          initial={{ opacity: 0, x: -10 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.4, delay: index * 0.15 + 0.8 }}
                        >
                          <div className="w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center border-2 border-[#003DA5]/20">
                            <ArrowRight className="w-4 h-4 text-[#003DA5]" />
                          </div>
                        </motion.div>
                      )}
                    </article>
                  </motion.div>
                )
              })}
            </motion.div>
          </div>

          {/* Bottom Stats */}
          <motion.div
            className="text-center mt-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 1.2 }}
          >
            <div className="inline-flex items-center gap-8 bg-white/80 backdrop-blur-sm px-8 py-4 rounded-2xl border border-white/50 shadow-lg">
              <div className="text-center">
                <div className="text-2xl font-bold text-[#003DA5]">5 Steps</div>
                <div className="text-sm text-muted-foreground">Simple Process</div>
              </div>
              <div className="w-px h-8 bg-slate-200"></div>
              <div className="text-center">
                <div className="text-2xl font-bold text-[#FF7A00]">24-48hrs</div>
                <div className="text-sm text-muted-foreground">Average Time</div>
              </div>
              <div className="w-px h-8 bg-slate-200"></div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">100%</div>
                <div className="text-sm text-muted-foreground">Satisfaction</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  )
}